import { useState } from 'react'
import { plural } from '../../../Util/plural.ts'
import { sampleDisplayName } from '../../../Services/OrderWizard/sampleLabels.ts'
import { useAnswer } from './useAnswer.ts'
import { questionSeverity } from '../../../Services/OrderWizard/issues.ts'
import { enteredPrimersUsable, primersMissingLeads, primersMissingSubject } from '../../../Services/OrderWizard/primerCheck.ts'
import { InlineWarning } from './Primitives.tsx'
import { answerClass, QuestionCardFrame } from './QuestionCardFrame.tsx'
import type { CardProps } from './QuestionCardFrame.tsx'
import { SheetPicker } from './SheetPicker.tsx'
import { usePrimerPasteRequest } from './primerPasteRequest.ts'
import { estimateShortfall } from '../../../Services/OrderWizard/estimateExplanation.ts'

const REASONS_SHOWN = 5

/**
 * Items without primers. Where the samples carry different tags, the untagged gene primer comes last: on
 * a negative control it matches the other samples' bled-over reads, so the control over-filters its pool.
 */
export const PrimersMissingCard = ({ draft, question, primary }: CardProps<'primers-missing'>) => {
  const answerWith = useAnswer(draft.id)
  const requestPaste = usePrimerPasteRequest()
  const suggestion = question.suggestion
  const tagged = suggestion?.kind === 'gene'
  const [forward, setForward] = useState(suggestion?.forward ?? '')
  const [reverse, setReverse] = useState(suggestion?.reverse ?? '')
  const answer = (primers: { forward: string; reverse: string }) =>
    answerWith({ kind: 'primers-missing', ...primers, groupKeys: question.groupKeys })
  const count = question.groupKeys.length
  const allControls = question.controls === count
  const who = primersMissingSubject(question)
  const names = question.groupKeys.map((key) => sampleDisplayName(draft, key)).join(', ')
  const valid = enteredPrimersUsable(forward, reverse, suggestion)
  const { sheet: sheetLeads, entry: entryLeads } = primersMissingLeads(question)
  const reasons = question.groupKeys.flatMap((key) => {
    const why = estimateShortfall(draft.groups[key]?.estimate)
    return why ? [{ key, name: sampleDisplayName(draft, key), why }] : []
  })

  const shared = suggestion?.kind === 'shared' && (
    <div className="space-y-0.5" data-testid="wizard-primers-option">
      <button
        type="button"
        className={answerClass(primary)}
        data-testid="wizard-primers-fill"
        onClick={() => answer({ forward: suggestion.forward, reverse: suggestion.reverse })}
      >
        {allControls ? 'Use the same primers as the samples' : 'Use the same primers as the other samples'}
      </button>
      <p className="font-mono text-xs text-slate-500">
        {suggestion.forward} / {suggestion.reverse}
      </p>
    </div>
  )
  const sheet = (
    <div className="flex flex-wrap items-center gap-x-3" data-testid="wizard-primers-option">
      <SheetPicker
        draft={draft}
        testId="wizard-primers-sheet"
        className={sheetLeads ? answerClass(primary) : 'btn btn-link btn-sm h-auto min-h-0 px-0 text-slate-600'}
      >
        {question.sheetUsed ? 'Drop the sample sheet again' : 'Add a sample sheet with the exact primers'}
      </SheetPicker>
      {requestPaste && (
        <button
          type="button"
          className="btn btn-link btn-sm h-auto min-h-0 px-0 text-slate-600"
          data-testid="wizard-primers-paste"
          onClick={requestPaste}
        >
          Paste from a spreadsheet
        </button>
      )}
    </div>
  )
  const entry = (
    <div className="space-y-1" data-testid="wizard-primers-option">
      <p className="text-xs text-slate-600">
        {tagged ? 'Or enter the exact primers: add the tag in front of the gene primer.' : 'Or enter the exact primers, tag included:'}
      </p>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <input
          aria-label="Forward primer"
          className="input input-bordered input-sm w-64 font-mono"
          value={forward}
          onChange={(event) => setForward(event.target.value)}
        />
        <input
          aria-label="Reverse primer"
          className="input input-bordered input-sm w-64 font-mono"
          value={reverse}
          onChange={(event) => setReverse(event.target.value)}
        />
        <button
          type="button"
          className={entryLeads ? answerClass(primary) : 'btn btn-sm'}
          disabled={!valid}
          data-testid="wizard-primers-manual"
          onClick={() => answer({ forward, reverse })}
        >
          Use these
        </button>
      </div>
    </div>
  )
  const untagged = tagged && (
    <div className="space-y-0.5 border-t border-slate-100 pt-2" data-testid="wizard-primers-option">
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        data-testid="wizard-primers-untagged"
        onClick={() => answer({ forward: suggestion.forward, reverse: suggestion.reverse })}
      >
        Use the gene primers without a tag
      </button>
      <span className="ml-2 font-mono text-xs text-slate-500">
        {suggestion.forward} / {suggestion.reverse}
      </span>
      {question.controls > 0 && (
        <InlineWarning layout="start" data-testid="wizard-primers-untagged-warning">
          {question.controls === 1 && allControls
            ? 'Without its tag, this control would match other samples’ reads and over-filter them.'
            : 'Without their tags, the controls would match other samples’ reads and over-filter them.'}
        </InlineWarning>
      )}
    </div>
  )

  return (
    <QuestionCardFrame title={`${who} ${count === 1 ? 'has' : 'have'} no primers`} tone={questionSeverity(question)}>
      <p className="mb-2 text-sm text-slate-600">
        {names}: neither a sheet nor the reads gave exact primers{tagged ? ', and the samples carry different tags' : ''}.
      </p>
      {reasons.length > 0 && (
        <ul className="mb-2 space-y-0.5 text-xs text-slate-600" data-testid="wizard-primers-why">
          {reasons.slice(0, REASONS_SHOWN).map(({ key, name, why }) => (
            <li key={key}>
              <span className="font-medium text-slate-700">{name}</span> — from the reads: {why}.
            </li>
          ))}
          {reasons.length > REASONS_SHOWN && <li>and {plural(reasons.length - REASONS_SHOWN, 'more item')}</li>}
        </ul>
      )}
      <div className="space-y-2">
        {suggestion?.kind === 'shared' ? (
          <>
            {shared}
            {entry}
            {question.sheetUsed && sheet}
          </>
        ) : (
          <>
            {question.sheetUsed && sheet}
            {entry}
            {!question.sheetUsed && sheet}
            {untagged}
          </>
        )}
      </div>
    </QuestionCardFrame>
  )
}
